import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfigurationService, PathsService, UserManagementService } from '@draeger-academy/common';
import { MustMatch } from '@draeger-academy/common/lib/helpers/must-match.helper';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormsUtils } from '../../form-builder/shared/forms.utils';
import { GenericModalService } from '../../generic-modal';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';

@Component({
    selector: 'da-reset-password-form',
    templateUrl: './reset-password-form.component.html',
    styleUrls: ['./reset-password-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ResetPasswordFormComponent implements OnInit {
    @ViewChild('resetPasswordModal', { static: true }) private _resetPasswordModal;
    changePasswordForm: UntypedFormGroup;
    loading = false;
    submitted = false;
    message = false;
    showMessage = false;
    hasFocusCurrentPassword = false;
    hasFocusNewPassword = false;
    hasFocusRenewPassword = false;
    success = false;
    translationPrefix = 'resetPassword';
    validationString: string;
    private _modalId: string;
    public linkForgotPassword: string;
    public targetLink: any;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private _configurationService: ConfigurationService,
        private _pathsService: PathsService,
        private _userManagementService: UserManagementService,
        private _genericModalService: GenericModalService,
        private _localizeRouterService: LocalizeRouterService
    ) {}

    private _$loaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public get loaded$(): Observable<{}> {
        return this._$loaded.asObservable();
    }

    private _$passwordResetSuccess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public get passwordResetSuccess$(): Observable<boolean> {
        return this._$passwordResetSuccess.asObservable();
    }

    private _$tokenIsInvalid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public get tokenIsInvalid$(): Observable<boolean> {
        return this._$tokenIsInvalid.asObservable();
    }

    private _$passwordResetError: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public get passwordResetError$(): Observable<boolean> {
        return this._$passwordResetError.asObservable();
    }

    // convenience getter for easy access to form fields
    linkPersonalData: string;
    get f() {
        return this.changePasswordForm.controls;
    }

    ngOnInit() {
        this.validateToken();
        this.linkPersonalData = this._pathsService.getAccountPersonalDataLink();
        if (this._configurationService.isPartnerPage()) {
            this.translationPrefix = 'account.resetPassword';
        }
        //Minimum eight characters, at least one uppercase letter, one lowercase letter, one number and one special character:
        const patternPassword = '^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$';
        // Validators.pattern(patternPassword); Validators.minLength(8)

        this.changePasswordForm = this.formBuilder.group(
            {
                newPassword: ['', [Validators.required, Validators.pattern(patternPassword), Validators.minLength(8)]],
                renewPassword: ['', [Validators.required]]
            },
            {
                validator: MustMatch('newPassword', 'renewPassword')
            }
        );
        this.linkForgotPassword = this._pathsService.getForgotPasswordLink();
        if (this._configurationService.isPartnerPage()) {
            this._configurationService.currentPortal$.subscribe((portalId) => {
                this.targetLink = '/partner/' + portalId + '/account';
            });
        } else {
            this.targetLink = this._localizeRouterService.translateRoute('/login');
        }    }

    public hasValue(fieldname) {
        const _input = fieldname;
        return _input.value ? FormsUtils.trim(_input.value.toString()) !== '' : false;
    }

    onSubmit() {
         this.submitted = true;
         // stop here if form is invalid
         if (this.changePasswordForm.invalid) {
             return;
         } else {
            this.resetPassword(this.validationString, this.f.newPassword.value, this.f.renewPassword.value);
         }

         this._$loaded.next(false);
    }

    private validateToken() {
        let url = window.location.href;
        if (url.substring(url.length-1) == "="){
                url = url.substring(0, url.length-1);
        }
        const token = url.slice(url.length -8);

        let str = url.substring(
            url.indexOf("?") + 1, 
            url.lastIndexOf("&")
        );

        str = str.substring(3);

        if (str.charAt(0) === '=') {
            str = str.substring(1);
        }

        if (str.charAt(0) === '%') {
            str = str.substring(3);
        }

        if (str.substring(str.length-1) == "="){
            str = str.substring(0, str.length-1);
    }
        this.validationString = str;

        const validationObject = {
            "str": str,
            "token": token
        }
        this._userManagementService.checkUserToken(validationObject).then(
            (data) => {
                if (!data) {
                    this._overlayResetPasswordModal();
                    this._$tokenIsInvalid.next(true);
                } else {
                    this._$tokenIsInvalid.next(false);
                }
            },
            (error) => {
                console.log(error);
            }
        );
    }
    private resetPassword(str, newpass, confirmPass){
        const resetPasswordObject = {
            "str": str,
            "newPassword": newpass,
            "confirmPassword": confirmPass
        }
        this._userManagementService.resetPassword(resetPasswordObject).then(
            () => {
                this._overlayResetPasswordModal();
                this._$passwordResetSuccess.next(true);
                this._$passwordResetError.next(false);
            },
            (error) => {
                console.log(error);
                this._overlayResetPasswordModal();
                this._$passwordResetError.next(true);
                this._$passwordResetSuccess.next(false);
            }
        );
    }

    private _overlayResetPasswordModal(): void {
        this._modalId = this._genericModalService.create(this._resetPasswordModal);
        this._genericModalService.open(this._modalId, this._resetPasswordModal, true, 'not-closeable', true);
    }

    public goToForgotPasswordPage(){
        this.closeModal();
    }

    public goToLoginPage() {
        this.closeModal();
    }

    public closeModal() {
        if (this._modalId) {
            this._genericModalService.close(this._modalId);
        }
    }
}
